import type { ProvenanceTouch, ProvenanceType } from '@vivport/backend/types';
import { MessageCircle, Settings, User } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { getCollectionLabel } from '@/lib/content-labels';
import { cn } from '@/lib/utils';
import { formatDate } from '@/utils/dateUtils';

interface ProvenanceTouchListProps {
  items: ProvenanceTouch[];
  onSelect: (touch: ProvenanceTouch) => void;
  className?: string;
}

interface SubtypeMeta {
  label: string;
  Icon: LucideIcon;
}

export const SUBTYPE_META: Record<ProvenanceType, SubtypeMeta> = {
  NEO_CHAT: { label: 'NeoChat', Icon: MessageCircle },
  PROCESS_RUN: { label: 'Process', Icon: Settings },
  MANUAL_SESSION: { label: 'Manual session', Icon: User },
};

function formatIdleDuration(fromIso: string, toIso: string): string {
  const from = new Date(fromIso).getTime();
  const to = new Date(toIso).getTime();
  if (!Number.isFinite(from) || !Number.isFinite(to)) return '';
  const diffMs = Math.max(0, to - from);
  const totalMin = Math.floor(diffMs / 60000);
  if (totalMin < 60) return `${totalMin}m`;
  const hours = Math.floor(totalMin / 60);
  const minutes = totalMin % 60;
  return minutes === 0 ? `${hours}h` : `${hours}h${minutes}m`;
}

function manualSessionStatePill(touch: ProvenanceTouch): string | null {
  if (touch.provenanceType !== 'MANUAL_SESSION') return null;
  if (touch.closedAt === null) return 'Active';
  const reason = touch.closeReason ?? 'closed';
  const idle = formatIdleDuration(touch.firstTouchedAt, touch.closedAt);
  return idle.length > 0 ? `Closed (${reason}, ${idle})` : `Closed (${reason})`;
}

export function ProvenanceTouchList({ items, onSelect, className }: ProvenanceTouchListProps) {
  if (items.length === 0) {
    return (
      <div className={cn('px-4 py-3 text-sm text-muted-foreground', className)}>
        No revisions yet.
      </div>
    );
  }
  return (
    <div className={cn('divide-y border-y', className)} data-testid="provenance-touch-list">
      {items.map((touch) => {
        const meta = SUBTYPE_META[touch.provenanceType];
        const statePill = manualSessionStatePill(touch);
        const date = new Date(touch.latestTouchedAt);
        return (
          <button
            key={touch.provenanceId}
            type="button"
            onClick={() => onSelect(touch)}
            className="w-full flex items-center space-x-4 px-4 text-left hover:bg-muted transition-colors"
            data-testid={`provenance-touch-row-${touch.provenanceId}`}
            title={date.toLocaleString()}
          >
            <div className="flex items-center py-3">
              <meta.Icon className="h-4 w-4 text-muted-foreground shrink-0" aria-hidden="true" />
            </div>
            <div className="flex w-44 min-w-0 shrink-0 items-center py-3">
              <span className="truncate text-sm text-muted-foreground">
                {touch.memberName ?? 'Unknown'}
              </span>
            </div>
            <div className="flex-1 min-w-0 py-3">
              <div className="flex min-w-0 items-center gap-2">
                <h3 className="min-w-0 text-sm font-normal text-muted-foreground truncate">
                  <span className="text-muted-foreground">{meta.label}</span>
                  <span className="mx-1.5 text-muted-foreground">—</span>
                  <span>{touch.title}</span>
                  {touch.group ? (
                    <span className="ml-1.5 text-muted-foreground">
                      in {getCollectionLabel(touch.group.kind)} {touch.group.code}
                    </span>
                  ) : null}
                </h3>
                {statePill ? (
                  <span className="text-xs text-muted-foreground rounded bg-muted px-1.5 py-0.5 shrink-0">
                    {statePill}
                  </span>
                ) : null}
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0 py-3">
              <span className="text-xs text-muted-foreground rounded bg-muted px-1.5 py-0.5">
                {touch.touchCount} {touch.touchCount === 1 ? 'edit' : 'edits'}
              </span>
              <time className="text-sm text-muted-foreground whitespace-nowrap">
                {formatDate(date)}
              </time>
            </div>
          </button>
        );
      })}
    </div>
  );
}
